import { useState, useEffect, useRef } from 'react';
import { Input, Button, Typography, Spin } from 'antd';
import './styles/SmsMessageList.less';
import imageSmsConversation from '../../../assets/images/sms_conversation.png';
import { Translation, getI18n } from 'react-i18next';
import UserAvatar from '../../../elements/containers/UserAvatarContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserPlus,
  faClipboardCheck,
  faEye,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { faComment } from '@fortawesome/free-regular-svg-icons';
import TransferPopover from '../containers/TransferPopoverContainer';
import moment from 'moment-timezone';
import notification from '../../../elements/lib/NotificationWrapper';

const { TextArea } = Input;
const { Title, Text } = Typography;

const SmsMessageList = ({
  selectedSmsConversation,
  onOpenProfile,
  list,
  partnershipId,
  limit,
  order,
  load,
  claim,
  resolve,
  reopen,
  markAsRead,
  create,
  smsConversationName,
  isProfileOpen,
  profileId,
  isLoading,
  total,
  smsConversationId,
  tz,
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
  const [previousScrollTop, setPreviousScrollTop] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isLoadingRef = useRef(false);
  const scrollAdjustmentTimeoutRef = useRef(null);
  const hasMarkedAsReadRef = useRef(false);
  const lastConversationIdRef = useRef(null);

  useEffect(() => {
    if (selectedSmsConversation?.id) {
      if (lastConversationIdRef.current !== selectedSmsConversation.id) {
        hasMarkedAsReadRef.current = false;
        lastConversationIdRef.current = selectedSmsConversation.id;
      }

      setCurrentPage(1);
      setIsInitialLoad(true);
      load(partnershipId, selectedSmsConversation.id, 1, limit, order);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnershipId, selectedSmsConversation?.id]);

  useEffect(() => {
    if (
      !selectedSmsConversation?.id ||
      !list ||
      list.length === 0 ||
      hasMarkedAsReadRef.current ||
      isInitialLoad
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const lastMessage = list[list.length - 1];

      if (lastMessage?.id && !hasMarkedAsReadRef.current) {
        hasMarkedAsReadRef.current = true;
        markAsRead(partnershipId, selectedSmsConversation.id, {
          last_read_sms_message_id: lastMessage.id,
        });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    selectedSmsConversation?.id,
    list,
    partnershipId,
    markAsRead,
    isInitialLoad,
  ]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (list && list.length > 0 && isInitialLoad) {
      setTimeout(() => {
        scrollToBottom();
        setIsInitialLoad(false);
      }, 0);
    }
  }, [list, isInitialLoad]);

  useEffect(() => {
    if (
      !isInitialLoad &&
      previousScrollHeight > 0 &&
      messagesContainerRef.current
    ) {
      const container = messagesContainerRef.current;

      if (scrollAdjustmentTimeoutRef.current) {
        clearTimeout(scrollAdjustmentTimeoutRef.current);
      }

      scrollAdjustmentTimeoutRef.current = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDiff = newScrollHeight - previousScrollHeight;

            container.scrollTop = previousScrollTop + heightDiff;

            setPreviousScrollHeight(0);
            setPreviousScrollTop(0);
            scrollAdjustmentTimeoutRef.current = null;
          });
        });
      }, 100);
    }
  }, [list, isInitialLoad, previousScrollHeight, previousScrollTop]);

  useEffect(() => {
    if (!isLoading) {
      isLoadingRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    return () => {
      if (scrollAdjustmentTimeoutRef.current) {
        clearTimeout(scrollAdjustmentTimeoutRef.current);
      }
    };
  }, []);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight } = e.target;

    if (
      scrollTop < 200 &&
      !isLoading &&
      !isLoadingRef.current &&
      list.length < total
    ) {
      isLoadingRef.current = true;
      setPreviousScrollHeight(scrollHeight);
      setPreviousScrollTop(scrollTop);
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      load(partnershipId, selectedSmsConversation.id, nextPage, limit, order);
    }
  };

  const handleSendMessage = () => {
    if (message.trim() && smsConversationId && !isSending) {
      const messageData = {
        body: message.trim(),
      };

      setIsSending(true);

      create(partnershipId, smsConversationId, messageData, (success) => {
        setIsSending(false);
        if (success) {
          notification.success(
            getI18n().t('sms_conversation_create_message_success')
          );

          setMessage('');
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        } else {
          notification.error(
            getI18n().t('sms_conversation_create_message_failure')
          );
        }
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getMessageStyle = (msg) => {
    const senderKind = msg.sender_kind;

    if (senderKind === 1 || senderKind === 2) {
      return {
        backgroundColor: '#E5F1FF',
        align: 'left',
        isOwn: false,
      };
    }

    if (senderKind === 3) {
      const isMe = msg?.profile?.id === profileId;
      return {
        backgroundColor: isMe ? '#E5ECF4' : '#FDE1B6',
        align: isMe ? 'right' : 'left',
        isOwn: isMe,
      };
    }

    if (senderKind === 4) {
      return {
        backgroundColor: '#E8F5E9',
        align: 'left',
        isOwn: false,
      };
    }

    if (senderKind === 5) {
      return {
        isSeparator: true,
      };
    }

    return {
      backgroundColor: '#E5F1FF',
      align: 'left',
      isOwn: false,
    };
  };

  const getSenderName = (msg) => {
    if (msg.sender_kind === 3) {
      const isMe = msg?.profile?.id === profileId;
      if (isMe) return 'You';

      if (msg?.profile?.first_name || msg?.profile?.last_name) {
        const firstName = msg.profile.first_name || '';
        const lastName = msg.profile.last_name || '';
        return `${firstName} ${lastName}`.trim();
      }

      return msg.sender_name || 'User';
    }

    if (msg.sender_kind === 1 || msg.sender_kind === 2) {
      if (
        selectedSmsConversation?.profile?.first_name ||
        selectedSmsConversation?.profile?.last_name
      ) {
        const firstName = selectedSmsConversation.profile.first_name || '';
        const lastName = selectedSmsConversation.profile.last_name || '';
        return `${firstName} ${lastName}`.trim();
      }

      return (
        selectedSmsConversation?.target_phone_number ||
        msg.sender_name ||
        'Client'
      );
    }

    if (msg.sender_kind === 4) {
      return 'Bot';
    }

    return msg.sender_name || 'Unknown';
  };

  const formatTimestamp = (sentAt) => {
    if (!sentAt) return '';
    const userTz = tz || 'America/Los_Angeles';
    const m = moment(sentAt).tz(userTz);
    const today = moment().tz(userTz).startOf('day');
    const yesterday = moment().tz(userTz).subtract(1, 'day').startOf('day');

    // Get current timezone abbreviation (not the message date's timezone)
    const now = moment().tz(userTz);
    const tzAbbr = now.zoneAbbr();
    const offset = now.format('Z').slice(0, 3);
    const gmtLabel = `${tzAbbr}-GMT${offset}`;

    if (m.isSame(today, 'day')) {
      return `${m.format('h:mm a')} ${gmtLabel}`;
    } else if (m.isSame(yesterday, 'day')) {
      return `Yesterday, ${m.format('h:mm a')} ${gmtLabel}`;
    } else {
      return `${m.format('MMM D, h:mm a')} ${gmtLabel}`;
    }
  };

  const claimSmsConversation = () => {
    if (smsConversationId) {
      claim(partnershipId, smsConversationId, {}, (success) => {
        if (success) {
          notification.success(getI18n().t('sms_conversation_claim_success'));
        } else {
          notification.error(getI18n().t('sms_conversation_claim_failure'));
        }
      });
    }
  };

  const resolveSmsConversation = () => {
    if (smsConversationId) {
      resolve(partnershipId, smsConversationId, {}, (success) => {
        if (success) {
          notification.success(getI18n().t('sms_conversation_resolve_success'));
        } else {
          notification.error(getI18n().t('sms_conversation_resolve_failure'));
        }
      });
    }
  };

  const reopenSmsConversation = () => {
    if (smsConversationId) {
      reopen(partnershipId, smsConversationId, {}, (success) => {
        if (success) {
          notification.success(getI18n().t('sms_conversation_reopen_success'));
        } else {
          notification.error(getI18n().t('sms_conversation_reopen_failure'));
        }
      });
    }
  };

  const getEventText = (msg) => {
    const eventType = msg.event_type;

    // Check if actor is current user
    const isActorCurrentUser = msg.event_actor_profile?.id === profileId;
    const actorFirstName = msg.event_actor_profile?.first_name || '';
    const actorLastName = msg.event_actor_profile?.last_name || '';
    const actorName = isActorCurrentUser
      ? 'You'
      : `${actorFirstName} ${actorLastName}`.trim() || 'Unknown';

    // Check if target is current user
    const isTargetCurrentUser = msg.event_target_profile?.id === profileId;
    const targetFirstName = msg.event_target_profile?.first_name || '';
    const targetLastName = msg.event_target_profile?.last_name || '';
    const targetName = isTargetCurrentUser
      ? 'you'
      : `${targetFirstName} ${targetLastName}`.trim() || 'Unknown';

    const createdAt = msg.created_at
      ? (() => {
          const userTz = tz || 'America/Los_Angeles';
          const m = moment(msg.created_at).tz(userTz);
          // Get current timezone abbreviation (not the event date's timezone)
          const now = moment().tz(userTz);
          const tzAbbr = now.zoneAbbr();
          const offset = now.format('Z').slice(0, 3);
          const gmtLabel = `${tzAbbr}-GMT${offset}`;
          return `${m.format('M/D/YYYY [at] h:mm A')} ${gmtLabel}`;
        })()
      : '';
    // TODO: Add translation
    switch (eventType) {
      case 1:
        return `${actorName} ${isActorCurrentUser ? 'have' : 'has'} been assigned to ${targetName}`;
      case 2:
        return `Claimed by ${actorName}`;
      case 3:
        return `Resolved by ${actorName} on ${createdAt}`;
      case 4:
        return `Reopen by ${actorName} on ${createdAt}`;
      case 5:
        return 'Pending to assigned';
      default:
        return msg.body || 'Event';
    }
  };

  if (!selectedSmsConversation) {
    return (
      <Translation>
        {(t) => (
          <div className='sms-conversation-empty'>
            <div className='empty-state'>
              <div className='empty-icon'></div>
              <img src={imageSmsConversation} alt={t('app_name')} />
              <Title level={3}>
                {getI18n().t('sms_conversation_new_conversation')}
              </Title>
              <p>
                {getI18n().t('sms_conversation_select_member')}
                <br />
                {getI18n().t('sms_conversation_new_message')}
              </p>
            </div>
          </div>
        )}
      </Translation>
    );
  }

  return (
    <Translation>
      {(t) => (
        <div className='sms-conversation'>
          <div className='conversation-header'>
            <div className='header-left'>
              <UserAvatar user={selectedSmsConversation?.profile} size={40} />
              <div className='header-info'>
                <Title level={5}>
                  {smsConversationName(selectedSmsConversation)}
                  {selectedSmsConversation?.care_guide_name && (
                    <> {`(${selectedSmsConversation.care_guide_name})`}</>
                  )}
                </Title>
              </div>
            </div>
            <div className='header-actions'>
              <Button
                type='text'
                className='action-btn'
                onClick={claimSmsConversation}
              >
                <FontAwesomeIcon icon={faUserPlus} />
                {t('sms_conversation_claim')}
              </Button>
              <TransferPopover smsConversationId={smsConversationId} />

              {selectedSmsConversation?.conversation_status === 1 ? (
                <Button
                  type='text'
                  className='resolve-btn'
                  onClick={resolveSmsConversation}
                >
                  <FontAwesomeIcon icon={faClipboardCheck} />
                  {t('sms_conversation_resolve')}
                </Button>
              ) : (
                <Button
                  type='text'
                  className='reopen-btn'
                  onClick={reopenSmsConversation}
                >
                  <FontAwesomeIcon icon={faClipboardCheck} />
                  {t('sms_conversation_reopen')}
                </Button>
              )}
              {selectedSmsConversation?.profile && (
                <Button
                  type='text'
                  className={`action-btn ${isProfileOpen ? 'active' : ''}`}
                  onClick={onOpenProfile}
                >
                  <FontAwesomeIcon icon={faEye} />
                  {t('sms_conversation_info')}
                </Button>
              )}
            </div>
          </div>

          <div
            className='conversation-messages'
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {isLoading && list.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
              </div>
            )}

            {isLoading && list.length > 0 && (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <Spin size='small' />
              </div>
            )}

            {list.map((msg) => {
              const style = getMessageStyle(msg);

              if (style.isSeparator) {
                return (
                  <div key={msg.id} className='message-separator'>
                    <div className='separator-bubble'>
                      {msg.event_type === 3 && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className='separator-icon'
                        />
                      )}
                      <Text style={{ fontSize: 12, fontWeight: 500 }}>
                        {getEventText(msg)}
                      </Text>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={msg.id}
                  className={`message ${
                    style.isOwn ? 'message-own' : 'message-other'
                  }`}
                >
                  <div className='message-content'>
                    <div className='message-header'>
                      {style.isOwn ? (
                        <>
                          <span className='message-sender'>
                            {getSenderName(msg)},
                          </span>
                          <span className='message-timestamp'>
                            {formatTimestamp(msg.sent_at)}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className='message-sender'>
                            {getSenderName(msg)},
                          </span>
                          <span className='message-timestamp'>
                            {formatTimestamp(msg.sent_at)}
                          </span>
                        </>
                      )}
                    </div>
                    <div
                      className='message-text'
                      style={{ backgroundColor: style.backgroundColor }}
                    >
                      {msg.body}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className='conversation-input-wrapper'>
            <TextArea
              placeholder='Type a message...'
              autoSize={{ minRows: 1, maxRows: 4 }}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              className='conversation-textarea'
            />
            <Button
              onClick={handleSendMessage}
              disabled={!message.trim() || isSending}
              loading={isSending}
              className='conversation-send-btn'
            >
              <FontAwesomeIcon icon={faComment} />
              {t('sms_conversation_send_sms')}
            </Button>
          </div>
        </div>
      )}
    </Translation>
  );
};

export default SmsMessageList;
